import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { ICartItemStatus, PriceCartService } from '../cart/price-cart.service';
import { FormattingService } from '../../../shared/services/formatting.service';
import * as _ from 'underscore';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../shared/formly/ga-formly.service';
import { UntypedFormGroup } from '@angular/forms';
import { util } from '../../../shared/util';
import { IOrderLeasePrices, IOrderPrices } from './order-entry-data.service';
import { IPrice } from '../../../../type_definitions/datatypes';

interface IOverviewItemsModel {
  acslFee?:string;
  lamFee?:string;
}

@Component({
  selector: 'order-entry-overview-items',
  template: `
    <div scrollTarget="overviewCart" class="order-overview form-horizontal" *ngIf="cartStatus">
      <formly [form]="form" [fields]="fields" [model]="model" class="order-fees col-md-3 float-end" *ngIf="model.acslFee || model.lamFee"></formly>
      <table class="table table-striped cart" [ngClass]="{'cart-with-warnings': cartHasWarnings}">
        <thead>
          <tr *ngIf="cartStatus?.length">
            <th translate>ORDERENTRY.OVERVIEW.PART</th>
            <th translate>GA.GENERAL.DESCRIPTION</th>
            <th class="text-center" translate>GA.GENERAL.QUANTITY</th>
            <th translate *ngIf="showLinesTaxPercentages">ORDERENTRY.OVERVIEW.TAX</th>
            <th class="lease-period" translate *ngIf="showOnlyLeasingPrices">ORDERENTRY.OVERVIEW.LEASE_PERIOD</th>
            <th class="lease-price" translate *ngIf="showOnlyLeasingPrices">ORDERENTRY.OVERVIEW.LEASE_PRICE</th>
            <th colspan="2"></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let item of cartStatus; trackBy: trackByFunction">
            <td class="part">
              <span class="badge partnumber">
                {{ item.product.part }}<span *ngIf="item.product.partSuffix">#{{ item.product.partSuffix }}</span>
              </span>
            </td>
            <td class="title">
              <info-link [(linkVariable)]="item.showInfo"></info-link>
              <span>{{ item.product.shortDescription }}</span>
              <div class="info-row" *ngIf="item.showInfo">{{ item.product.description }}</div>
              <div class="part-configuration" *ngIf="item.product.partSuffixFullDescription">{{ item.product.partSuffixFullDescription }}</div>
            </td>
            <td class="amount text-center">
              <span>{{ item.cartCount }}</span>
            </td>
            <td class="text-center" *ngIf="showLinesTaxPercentages">{{ item.product.taxPercentage }} %</td>
            <td class="lease-period" *ngIf="showOnlyLeasingPrices">
              <span translate [translateParams]="{ months: item.product.leasePeriod }" *ngIf="item.product.leasePeriod">CATALOG.MONTHS_LABEL</span>
            </td>
            <td class="price lease-price-monthly bold" *ngIf="showOnlyLeasingPrices" [ngClass]="{strike: item.product.inValid}">
              <div>{{ getLeasePrice(item.product.unitLeasePrice) }}</div>
            </td>
            <td class="price" colspan="2" [ngClass]="{strike: item.product.inValid}">
              <ng-container *ngIf="!showOnlyLeasingPrices">
                <div *ngIf="item.orderLineStatus === 'NEWPRICE'" class="strike">{{ formatAmount(item.product.initialUnitPrice) }}</div>
                <div *ngIf="item.product.price.amount">{{ formatAmount(item.product.price.amount) }}</div>
              </ng-container>

              <ng-container *ngIf="showOnlyLeasingPrices">
                <div>{{ getLeasePrice(item.product.unitLeasePriceTotal) }}</div>
              </ng-container>
            </td>
            <td class="warnings errorText" *ngIf="cartHasWarnings">
              <div *ngIf="item.warning">
                <i class="fa fa-warning"></i> {{ item.warning.text }} <a href (click)="item.warning.popUp()" translate>GA.GENERAL.SEE_MORE</a>
              </div>
            </td>
          </tr>
        </tbody>
        <tfoot *ngIf="showTotals">
          <tr>
            <td class="empty" colspan="2"></td>
            <td class="empty" *ngIf="showLinesTaxPercentages"></td>
            <td class="pricelabel text-center"><span translate>ORDERENTRY.CART.SUBTOTAL</span>:</td>
            <td class="pricelabel" *ngIf="showOnlyLeasingPrices"></td>
            <td class="price bold" *ngIf="showOnlyLeasingPrices">{{ formatAmount(leasePrices?.monthly.subtotal) }}</td>
            <td class="price">{{ showOnlyLeasingPrices? formatAmount(leasePrices?.total.subtotal) : formatAmount(purchasePrices?.subtotal) }}</td>
            <td *ngIf="cartHasWarnings"></td>
          </tr>
          <tr *ngIf="purchasePrices?.chargesTotal || leasePrices?.total?.chargesTotal">
            <td class="empty" colspan="2"></td>
            <td class="empty" *ngIf="showLinesTaxPercentages"></td>
            <td class="pricelabel text-center"><span translate>ORDERENTRY.CART.CHARGES</span>:</td>
            <td class="pricelabel" *ngIf="showOnlyLeasingPrices"></td>
            <td class="price bold" *ngIf="showOnlyLeasingPrices">{{ formatAmount(leasePrices?.monthly.chargesTotal) }}</td>
            <td class="price">{{ showOnlyLeasingPrices? formatAmount(leasePrices?.total.chargesTotal) : formatAmount(purchasePrices?.chargesTotal) }}</td>
            <td *ngIf="cartHasWarnings"></td>
          </tr>
          <tr>
            <td class="empty" colspan="2"></td>
            <td class="empty" *ngIf="showLinesTaxPercentages"></td>
            <td class="pricelabel text-center"><span translate>ORDERENTRY.CART.VAT_TAXES</span>:</td>
            <td class="pricelabel" *ngIf="showOnlyLeasingPrices"></td>
            <td class="price bold" *ngIf="showOnlyLeasingPrices">{{ formatAmount(leasePrices?.monthly.tax) }}</td>
            <td class="price">{{ showOnlyLeasingPrices? formatAmount(leasePrices?.total.tax) : formatAmount(purchasePrices?.tax) }}</td>
            <td *ngIf="cartHasWarnings"></td>
          </tr>
          <tr>
            <td class="empty" colspan="2"></td>
            <td class="empty" *ngIf="showLinesTaxPercentages"></td>
            <td class="pricelabel text-center"><span translate>ORDERENTRY.CART.TOTAL</span>:</td>
            <td class="pricelabel" *ngIf="showOnlyLeasingPrices"></td>
            <td class="price bold" *ngIf="showOnlyLeasingPrices">{{ formatAmount(leasePrices?.monthly.total) }}</td>
            <td class="price">{{ showOnlyLeasingPrices? formatAmount(leasePrices?.total.total) : formatAmount(purchasePrices?.total) }}</td>
            <td *ngIf="cartHasWarnings"></td>
          </tr>
        </tfoot>
      </table>
    </div>
  `,
  styleUrls: ['../cart/cart.scss'],
  encapsulation: ViewEncapsulation.None
})
export class OrderEntryOverviewItemsComponent implements OnInit, OnChanges {
  @Input() public purchasePrices:IOrderPrices;
  @Input() public leasePrices:IOrderLeasePrices;
  @Input() public showLease:boolean;
  @Input() public lamFee:string;
  @Input() public acslFee:string;
  @Input() public hidePrices:boolean;

  public model:IOverviewItemsModel = {};
  public fields:IExtraFieldConfigurationObject[];
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public cartStatus:ICartItemStatus[];
  public showLinesTaxPercentages:boolean;
  public cartHasWarnings:boolean;

  constructor(
    private formattingService:FormattingService,
    private priceCartService:PriceCartService,
    private formlyService:GaFormlyService
  ) {
    /**/
  }

  public ngOnInit():void {
    this.cartHasWarnings = this.priceCartService.hasWarnings();
    this.cartStatus = this.priceCartService.cartStatus;
    this.showLinesTaxPercentages = _.filter(this.cartStatus, (item:ICartItemStatus) => item.product.taxPercentage > 0).length > 0;
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes.purchasePrices) {
      this.purchasePrices = changes.purchasePrices.currentValue;
    }
    if (changes.leasePrices) {
      this.leasePrices = changes.leasePrices.currentValue;
    }
    if (changes.showLease) {
      this.showLease = changes.showLease.currentValue;
    }
    if (changes.lamFee) {
      this.lamFee = changes.lamFee.currentValue;
    }
    if (changes.acslFee) {
      this.acslFee = changes.acslFee.currentValue;
    }
    if (changes.lamFee || changes.acslFee) {
      this.onFeesChange(this.lamFee, this.acslFee);
    }
  }

  public formatAmount(price:number):string {
    return this.formattingService.formatAmount(price, this.priceCartService.currency);
  }

  public trackByFunction(index:number, item:ICartItemStatus):string {
    return <string>item.product.itemId;
  }

  public get showTotals():boolean {
    return !this.hidePrices || this.showLease;
  }

  public get showOnlyLeasingPrices():boolean {
    return this.showLease && this.hidePrices;
  }

  public getLeasePrice(price:IPrice|number):string {
    return this.formatAmount(_.isObject(price) ? (<IPrice>price)?.amount : <number>price);
  }

  private onFeesChange(lamFee:string, acslFee:string): void {
    this.model = { lamFee, acslFee };

    if (!this.fields?.length && (util.notNullOrUndefinedOrEmpty(this.model.lamFee) || util.notNullOrUndefinedOrEmpty(this.model.acslFee))) {
      this.fields = this.createFields();
    }

    this.form.patchValue(this.model);
  }

  private createFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aValue('lamFee', 'LAM Fee:')
        .withLabelClass('col-md-6')
        .withValueClass('col-md-6 text-end')
        .withHideExpression(() => util.isNullOrUndefinedOrEmpty(this.model.lamFee))
        .build(),
      this.formlyService.aValue('acslFee', 'ACSL Fee:')
        .withLabelClass('col-md-6')
        .withValueClass('col-md-6 text-end')
        .withHideExpression(() => util.isNullOrUndefinedOrEmpty(this.model.acslFee))
        .build()
    ];
  }
}